/**
 * GradeDistributionChart Component
 *
 * Displays a horizontal bar chart showing the distribution of community grade submissions.
 * Only shows the range of grades that have been submitted (min to max).
 * Skipped grades in the range are shown with 0 count for continuity.
 */

import { type CSSProperties } from 'react';
import { V_GRADES, type VGrade, type GradeDisplayPreference } from '@flash/shared';
import type { GradeDistribution } from '@/hooks/useRouteGradeSubmissions';
import { vToFont } from '@/utils/gradeConversion';

/**
 * Props for GradeDistributionChart component
 */
interface GradeDistributionChartProps {
  /** Grade distribution (count per grade) */
  distribution: GradeDistribution;
  /** Min grade in the distribution (determines display range) */
  minGrade: VGrade | null;
  /** Max grade in the distribution (determines display range) */
  maxGrade: VGrade | null;
  /** Total number of submissions */
  totalCount: number;
  /** Official/setter grade for highlighting */
  officialGrade?: VGrade | null;
  /** Consensus grade calculated from community votes */
  consensusGrade?: VGrade | null | undefined;
  /** User's submitted grade for highlighting */
  userGrade?: VGrade | null;
  /** Grade display preference ('v-scale' or 'font') */
  displayPreference?: GradeDisplayPreference;
}

/**
 * Get grade index for range calculations
 */
const getGradeIndex = (grade: VGrade): number => {
  return V_GRADES.indexOf(grade);
};

/**
 * GradeDistributionChart shows community grade submissions as a bar chart
 *
 * @example
 * ```tsx
 * <GradeDistributionChart
 *   distribution={distribution}
 *   minGrade="V2"
 *   maxGrade="V5"
 *   totalCount={10}
 *   officialGrade="V3"
 *   userGrade="V4"
 * />
 * ```
 */
export const GradeDistributionChart = ({
  distribution,
  minGrade,
  maxGrade,
  totalCount,
  officialGrade,
  consensusGrade,
  userGrade,
  displayPreference = 'v-scale',
}: GradeDistributionChartProps) => {
  const scale = displayPreference === 'font' ? 'font' : 'v-scale';

  /** Format grade for display based on scale preference */
  const formatGrade = (grade: VGrade): string => {
    return scale === 'font' ? vToFont(grade) : grade;
  };
  // If no submissions, show empty state
  if (!minGrade || !maxGrade || totalCount === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyTextStyle}>No grade votes yet</p>
        <p style={emptySubtextStyle}>Be the first to suggest a grade!</p>
      </div>
    );
  }

  // Get grades in the display range (min to max, inclusive)
  const minIndex = getGradeIndex(minGrade);
  const maxIndex = getGradeIndex(maxGrade);
  const displayGrades = V_GRADES.slice(minIndex, maxIndex + 1);

  // Find the max count for scaling bars
  const maxCount = Math.max(...displayGrades.map((g) => distribution[g]), 1);

  return (
    <div style={containerStyle}>
      <div style={chartStyle}>
        {displayGrades.map((grade) => {
          const count = distribution[grade];
          const percentage = (count / maxCount) * 100;
          const isOfficial = grade === officialGrade;
          const isUserGrade = grade === userGrade;

          return (
            <div key={grade} style={rowStyle}>
              {/* Grade label */}
              <div
                style={{
                  ...labelStyle,
                  ...(isOfficial ? labelOfficialStyle : {}),
                  ...(isUserGrade ? labelUserStyle : {}),
                }}
              >
                {formatGrade(grade)}
                {isOfficial && <span style={officialBadgeStyle}>setter</span>}
              </div>

              {/* Bar container */}
              <div style={barContainerStyle}>
                <div
                  style={{
                    ...barStyle,
                    width: count > 0 ? `${Math.max(percentage, 8)}%` : '0%',
                    ...(isUserGrade ? barUserStyle : {}),
                  }}
                />
              </div>

              {/* Count */}
              <div
                style={{
                  ...countStyle,
                  ...(isUserGrade ? countUserStyle : {}),
                }}
              >
                {count}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary with Consensus Grade */}
      <div style={summaryStyle}>
        {consensusGrade ? (
          <>
            <span style={consensusLabelStyle}>Consensus:</span>
            <span style={consensusGradeDisplayStyle}>{formatGrade(consensusGrade)}</span>
            {officialGrade && consensusGrade !== officialGrade && (
              <span style={gradeDiffStyle}>
                {getGradeIndex(consensusGrade) > getGradeIndex(officialGrade)
                  ? '(harder)'
                  : '(easier)'}
              </span>
            )}
            <span style={voteCountStyle}>
              ({totalCount} {totalCount === 1 ? 'vote' : 'votes'})
            </span>
          </>
        ) : (
          <>
            <span style={summaryTextStyle}>
              {totalCount} {totalCount === 1 ? 'vote' : 'votes'}
            </span>
            {totalCount > 0 && totalCount < 3 && (
              <span style={needMoreHintStyle}>({3 - totalCount} more for consensus)</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const chartStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: '24px',
};

const labelStyle: CSSProperties = {
  width: '48px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
};

const labelOfficialStyle: CSSProperties = {
  color: 'var(--color-text-primary, #1f2937)',
};

const labelUserStyle: CSSProperties = {
  color: '#3b82f6',
};

const officialBadgeStyle: CSSProperties = {
  fontSize: '0.625rem',
  fontWeight: 500,
  color: '#6b7280',
  backgroundColor: '#f3f4f6',
  padding: '1px 4px',
  borderRadius: '2px',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const barContainerStyle: CSSProperties = {
  flex: 1,
  height: '20px',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  borderRadius: '4px',
  overflow: 'hidden',
};

const barStyle: CSSProperties = {
  height: '100%',
  backgroundColor: '#3b82f6',
  borderRadius: '4px',
  transition: 'width 0.3s ease',
  minWidth: 0,
};

const barUserStyle: CSSProperties = {
  backgroundColor: '#3b82f6',
};

const countStyle: CSSProperties = {
  width: '24px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  textAlign: 'right',
  flexShrink: 0,
};

const countUserStyle: CSSProperties = {
  color: '#3b82f6',
  fontWeight: 600,
};

const summaryStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '4px',
};

const summaryTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
};

const consensusLabelStyle: CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
};

const consensusGradeDisplayStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#22c55e',
};

const gradeDiffStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#f59e0b',
};

const voteCountStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const needMoreHintStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: '#f59e0b',
};

const emptyStateStyle: CSSProperties = {
  textAlign: 'center',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderRadius: '8px',
};

const emptyTextStyle: CSSProperties = {
  margin: '0 0 4px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
};

const emptySubtextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: 'var(--color-text-tertiary, #9ca3af)',
};

export default GradeDistributionChart;
